import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import { useGPS } from '../../contexts/GPSContext';
import CustomBlock from './CustomBlock';
import ErrorBoundary from '../ui/ErrorBoundary';

// One block failing to render degrades to this card; the rest of the page keeps working.
const BlockFallback = ({ title }) => (
  <div className="h-full rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
    <div className="font-semibold">{title || 'Bloc'}</div>
    <div className="mt-1 text-xs">Ce bloc n'a pas pu être affiché.</div>
  </div>
);

// Section layout -> grid. The config names the intent; the page owns the CSS.
const SECTION_GRID = {
  'grid-1': 'grid-cols-1',
  'grid-2': 'grid-cols-1 lg:grid-cols-2',
  'grid-3': 'grid-cols-1 lg:grid-cols-3',
};

// A block's span within its section's grid.
const spanClass = (span, layout) => {
  if (span === 1) return '';
  if (layout === 'grid-3') return 'lg:col-span-3';
  return 'lg:col-span-2';
};

/**
 * Renders one sub-menu of the config-driven page.
 *
 * The page config and the active sub-menu come from GPSViewer: the sub-menus live in
 * the sidebar (indented under "Point de ventes"), not in a tab bar above the content,
 * so the owner of the sidebar owns that state.
 */
function GPSCustomPage({ page, activeTabId }) {
  const gps = useGPS();
  const projectKey = gps.selectedProjet?.value;

  // Every filter narrows the tickets, hence the points of sale (backend ticketFilters.js).
  const globalFilters = useMemo(() => {
    const pick = (selection) => (selection || []).map((option) => option.value);
    const filters = {};
    const entries = {
      brand: pick(gps.selectedBrands),
      shopsign: pick(gps.selectedShopSigns),
      tiering: pick(gps.selectedTierings),
      sector: pick(gps.selectedSectors),
      request_type: pick(gps.selectedRequestTypes),
      furniture: pick(gps.selectedFurnitures),
      status: pick(gps.selectedStatus),
      intervention_point: pick(gps.selectedShops),
      problems: pick(gps.selectedProblems),
      flags: pick(gps.selectedCustomFilters),
    };

    Object.entries(entries).forEach(([key, values]) => {
      if (values.length > 0) filters[key] = values;
    });

    return filters;
  }, [
    gps.selectedBrands, gps.selectedShopSigns, gps.selectedTierings, gps.selectedSectors,
    gps.selectedRequestTypes, gps.selectedFurnitures, gps.selectedStatus, gps.selectedShops,
    gps.selectedProblems, gps.selectedCustomFilters,
  ]);

  if (!page) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[#5b3df0]" />
      </div>
    );
  }

  const tab = page.tabs.find((t) => t.id === activeTabId) || page.tabs[0];
  const syncedAt = gps.lastSyncDate ? dayjs(gps.lastSyncDate).format('DD/MM/YYYY à HH:mm') : null;

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-baseline justify-between gap-4">
        <div>
          <h1 className="m-0 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">{page.title}</h1>
          <div className="mt-1 text-[13px] text-gray-500 dark:text-gray-400">
            {tab.label} · {gps.selectedProjet?.label}
          </div>
        </div>
        {syncedAt && (
          <div className="text-xs text-gray-400 dark:text-gray-500">Synchronisé le {syncedAt}</div>
        )}
      </div>

      <div className="flex flex-col gap-[18px]">
        {tab.sections.map((section) => (
          <section key={section.id}>
            {section.title && (
              <h2 className="mb-3 text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {section.title}
              </h2>
            )}
            <div className={`grid ${SECTION_GRID[section.layout] || SECTION_GRID['grid-2']} items-stretch gap-[18px]`}>
              {section.blocks.map((blockId) => {
                const block = page.blocks[blockId];
                if (!block) return null;
                return (
                  <div key={blockId} className={spanClass(block.layout?.span, section.layout)}>
                    <ErrorBoundary fallback={<BlockFallback title={block.title} />} resetKeys={[gps.startDate, gps.endDate, globalFilters]}>
                      <CustomBlock
                        block={block}
                        projectId={projectKey}
                        pageId={page.id}
                        startDate={gps.startDate}
                        endDate={gps.endDate}
                        globalFilters={globalFilters}
                      />
                    </ErrorBoundary>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

export default GPSCustomPage;
